import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { usePlatform, useContactInfo } from '@/hooks'
import LanguageSwitcher from '../shared/LanguageSwitcher'
import ContactList from '../shared/ContactList'
import logo from '@/assets/logo.jpg'

const NAV_ITEMS = [
  { id: 'home', path: '/', icon: 'home' },
  { id: 'vehicles', path: '/vehicles', icon: 'directions_car' },
  { id: 'about', path: '/about', icon: 'info' },
  { id: 'trackOrder', path: '/track-search', icon: 'local_shipping' },
] as const

const APP_DRAWER_NAV = [
  { id: 'home', path: '/', icon: 'home' },
  { id: 'vehicles', path: '/vehicles', icon: 'directions_car' },
  { id: 'inquiry', path: '/inquiry', icon: 'add_circle' },
  { id: 'about', path: '/about', icon: 'info' },
  { id: 'trackOrder', path: '/track-search', icon: 'local_shipping' },
] as const

export default function Header() {
  const { t } = useTranslation()
  const platform = usePlatform()
  const contact = useContactInfo()
  const isPC = platform === 'pc'
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <header className="fixed top-0 left-0 right-0 bg-white border-b border-neutral-200 z-header">
      <div className={isPC ? 'max-w-7xl mx-auto px-6' : 'px-4'}>
        <div className={clsx('flex items-center justify-between', isPC ? 'h-20' : 'h-14')}>
          {/* Logo */}
          <Link to="/" className={clsx('flex items-center group', isPC ? 'space-x-3' : 'space-x-2')}>
            <img
              src={logo}
              alt={t('site.name')}
              className={clsx(
                'rounded-lg object-contain transition-all',
                isPC ? 'w-14 h-14 group-hover:scale-105' : 'w-10 h-10'
              )}
            />
            <div>
              <div className={clsx(
                'font-bold text-neutral-900',
                isPC ? 'text-lg md:text-xl' : 'text-base'
              )}>
                {t('site.name')}
              </div>
              {isPC && (
                <div className="text-xs text-neutral-500">{t('site.slogan')}</div>
              )}
            </div>
          </Link>

          {/* PC Navigation */}
          {isPC && (
            <nav className="hidden md:flex items-center space-x-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={clsx(
                    'flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm transition-all whitespace-nowrap',
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-neutral-700 hover:bg-blue-50 hover:text-blue-600'
                  )}
                >
                  <span className="material-icons-round text-xl">{item.icon}</span>
                  <span className="font-medium">{t(`nav.${item.id}`)}</span>
                </Link>
              ))}
            </nav>
          )}

          {/* Right Actions */}
          <div className={clsx('flex items-center', isPC ? 'space-x-4' : 'space-x-2')}>
            <LanguageSwitcher />

            {isPC && (
              <Link
                to="/inquiry"
                className="hidden lg:flex items-center space-x-2 px-5 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-all shadow-md hover:shadow-lg"
              >
                <span className="material-icons-round text-xl">chat</span>
                <span className="font-medium">{t('contact.quickContact')}</span>
              </Link>
            )}

            {/* APP Menu Button */}
            {!isPC && (
              <button
                onClick={() => setDrawerOpen(true)}
                className="p-2 rounded-lg hover:bg-neutral-100 transition"
              >
                <span className="material-icons-round text-neutral-700 text-2xl">menu</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* APP Right Drawer */}
      {!isPC && drawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[60]"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="absolute top-0 right-0 bottom-0 w-72 bg-white animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <img src={logo} alt={t('site.name')} className="w-12 h-12 rounded-lg object-contain" />
                  <span className="text-lg font-bold text-neutral-900">{t('site.name')}</span>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg"
                >
                  <span className="material-icons-round text-neutral-600">close</span>
                </button>
              </div>

              {/* Navigation */}
              <nav className="space-y-1">
                {APP_DRAWER_NAV.map((item) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => setDrawerOpen(false)}
                    className={clsx(
                      'flex items-center space-x-3 px-4 py-3 rounded-xl transition-all',
                      isActive(item.path)
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-neutral-700 hover:bg-blue-50 hover:text-blue-600'
                    )}
                  >
                    <span className="material-icons-round text-xl">{item.icon}</span>
                    <span className="font-medium">{t(`nav.${item.id}`)}</span>
                  </Link>
                ))}
              </nav>

              {/* Contact Section */}
              {contact && (
                <div className="mt-8">
                  <div className="text-sm font-semibold text-neutral-900 mb-3">
                    {t('contact.quickContact')}
                  </div>
                  <ContactList contact={contact} variant="card" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
